interface AdvancedTabProps {
  values: Record<string, string>;
  onUpdate: (key: string, value: string) => void;
}

export default function AdvancedTab({ values, onUpdate }: AdvancedTabProps) {
  const base = "w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400";

  const toggles = [
    { key: "allow_registration", label: "Allow public registration", desc: "Let visitors create accounts on the frontend." },
    { key: "enforce_https", label: "Enforce HTTPS", desc: "Redirect all HTTP traffic to HTTPS." },
    { key: "enable_captcha", label: "Enable CAPTCHA on forms", desc: "Add Google reCAPTCHA to enquiry forms." },
    { key: "cache_enabled", label: "Enable page caching", desc: "Cache public pages for faster loading." },
  ];

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-background-100">
          <div className="flex items-center gap-3">
            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-secondary-100 text-secondary-700">
              <i className="ri-settings-3-line"></i>
            </span>
            <div>
              <h3 className="font-heading text-base text-foreground-950">Advanced</h3>
              <p className="text-xs text-foreground-600">Power-user settings for security, caching, and features.</p>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Google Analytics ID</label>
              <input
                className={base}
                value={values.ga_id || ""}
                onChange={(e) => onUpdate("ga_id", e.target.value)}
                placeholder="G-XXXXXXXXXX"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Google Tag Manager ID</label>
              <input
                className={base}
                value={values.gtm_id || ""}
                onChange={(e) => onUpdate("gtm_id", e.target.value)}
                placeholder="GTM-XXXXXXX"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Facebook Pixel ID</label>
              <input
                className={base}
                value={values.fb_pixel_id || ""}
                onChange={(e) => onUpdate("fb_pixel_id", e.target.value)}
                placeholder="123456789012345"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Custom header script</label>
              <input
                className={base}
                value={values.custom_header_script || ""}
                onChange={(e) => onUpdate("custom_header_script", e.target.value)}
                placeholder="<script>...</script>"
              />
            </div>
          </div>

          <div className="space-y-3 pt-2">
            {toggles.map((t) => {
              const on = values[t.key] === "1" || values[t.key] === "true";
              return (
                <div
                  key={t.key}
                  className="flex items-center justify-between rounded-xl bg-background-100 border border-background-200 p-4"
                >
                  <div>
                    <p className="text-sm font-semibold text-foreground-950">{t.label}</p>
                    <p className="text-xs text-foreground-600">{t.desc}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onUpdate(t.key, on ? "0" : "1")}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      on ? "bg-primary-600" : "bg-background-300"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-background-50 transition-transform ${
                        on ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}